import React, { ReactNode, useCallback } from "react";
import { connect } from "react-redux";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import RoleEnum from "@mapmycustomers/shared/enum/Role";
import Visibility from "@mapmycustomers/shared/enum/Visibility";
import PinLegend from "@mapmycustomers/shared/types/map/PinLegend";
import { SelectField, SelectFieldProps } from "@mapmycustomers/ui";

import MapMode from "@app/scene/map/enums/MapMode";
import { getMapMode } from "@app/scene/map/store/selectors";
import { getPinLegendCreationVisibility, getRole } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import loggingService from "@app/util/logging";

import customOptionRenderer from "../utils/customOptionRenderer";

import styles from "./PinLegendSelectField.module.scss";

interface Props extends SelectFieldProps<PinLegend["id"]> {
  allowClear?: boolean;
  canCreateCustom: boolean;
  children?: ReactNode;
  className?: string;
  createCustomDisabled: boolean;
  icon: IconProp;
  label?: ReactNode;
  onChange?: (pinLegendId: PinLegend["id"]) => void;
  onCreateCustom?: () => void;
  options?: { label: ReactNode; value: PinLegend["id"] }[];
  value?: PinLegend["id"];
}

const PinLegendSelectField: React.FC<Props> = ({
  allowClear,
  canCreateCustom,
  children,
  className,
  createCustomDisabled,
  icon,
  label,
  onChange,
  onCreateCustom,
  options,
  value,
  ...props
}) => {
  const intl = useIntl();

  if (options && children) {
    loggingService.warning(
      `Please only specify either children or options in PinLegendSelectField, not both`
    );
  }

  const handleFilterOption = useCallback((inputValue: string, option?: any) => {
    return option.filterLabel?.toLowerCase().includes(inputValue.toLowerCase());
  }, []);

  return (
    <SelectField<PinLegend["id"]>
      {...props}
      allowClear={allowClear}
      dropdownMatchSelectWidth
      dropdownRender={
        canCreateCustom
          ? customOptionRenderer(
              intl,
              intl.formatMessage({
                id: "map.toolbar.popovers.pinSettings.legendSelectField.custom",
                defaultMessage: "Create Custom",
                description:
                  "Create Custom option title in Pin Legend select field in Pin Settings popover on the map page",
              }),
              onCreateCustom,
              createCustomDisabled
            )
          : undefined
      }
      filterOption={handleFilterOption}
      onChange={onChange}
      options={options}
      prefixIcon={<FontAwesomeIcon className={styles.icon} icon={icon} />}
      showSearch
      size="large"
      value={value}
    >
      {children}
    </SelectField>
  );
};

const mapStateToProps = (state: RootState) => {
  const pinLegendsCreation = getPinLegendCreationVisibility(state);
  const role = getRole(state);
  const canCreateCustom =
    (role?.key === RoleEnum.OWNER ||
      (role?.key === RoleEnum.MANAGER && pinLegendsCreation === Visibility.SHARED_WITH_MANAGERS) ||
      pinLegendsCreation === Visibility.SHARED_WITH_ORGANIZATION) &&
    role?.key !== RoleEnum.READ_ONLY;

  return {
    canCreateCustom,
    createCustomDisabled: getMapMode(state) === MapMode.LEAD_FINDER,
  };
};

export default connect(mapStateToProps, {})(PinLegendSelectField);
